import { useState } from 'react';
import { useBranding } from '../../contexts/BrandingContext';
import { useCmsPage } from '../../contexts/CmsPageContext';
import { getTenantSlug } from '../../lib/tenant';

interface FormField {
  label: string;
  fieldType: 'text' | 'email' | 'phone' | 'select' | 'textarea' | 'checkbox';
  required?: boolean;
  placeholder?: string;
  options?: string[];
}

interface FormProps {
  formTitle?: string;
  submitButtonText?: string;
  successMessage?: string;
  fields?: FormField[];
}

export function FormBlock({ props }: { props: FormProps }) {
  const branding = useBranding();
  const { pageSlug, spaceId } = useCmsPage();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [values, setValues] = useState<Record<string, string | boolean>>({});
  const fields = props.fields ?? [];

  function handleChange(label: string, value: string | boolean) {
    setValues((v) => ({ ...v, [label]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'x-tenant-slug': getTenantSlug(),
    };
    if (spaceId) headers['x-space-id'] = spaceId;

    try {
      const res = await fetch('/customer/portal/form-submissions', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          pageSlug: pageSlug || undefined,
          formTitle: props.formTitle || undefined,
          data: values,
        }),
      });

      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? 'Submission failed');
      }

      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <div className="py-8 px-4">
        <div className="max-w-lg mx-auto rounded-xl border border-green-200 bg-green-50 px-6 py-8 text-center">
          <svg
            className="w-10 h-10 text-green-500 mx-auto mb-3"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-green-800 font-medium text-base">
            {props.successMessage || 'Thank you! Your submission has been received.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="py-8 px-4">
      <form onSubmit={handleSubmit} className="max-w-lg mx-auto flex flex-col gap-5">
        {props.formTitle && (
          <h2 className="text-xl font-bold text-zinc-900">{props.formTitle}</h2>
        )}

        {fields.map((field, i) => (
          <div key={i} className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-zinc-700">
              {field.label}
              {field.required && <span className="text-red-500 ml-0.5">*</span>}
            </label>
            {field.fieldType === 'textarea' ? (
              <textarea
                required={field.required}
                placeholder={field.placeholder}
                rows={4}
                className="rounded-lg border border-zinc-300 px-3 py-2 text-sm text-zinc-800 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary,#4F46E5)] resize-none"
                value={(values[field.label] as string) ?? ''}
                onChange={(e) => handleChange(field.label, e.target.value)}
              />
            ) : field.fieldType === 'select' ? (
              <select
                required={field.required}
                className="rounded-lg border border-zinc-300 px-3 py-2 text-sm text-zinc-800 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary,#4F46E5)]"
                value={(values[field.label] as string) ?? ''}
                onChange={(e) => handleChange(field.label, e.target.value)}
              >
                <option value="">Select…</option>
                {(field.options ?? []).map((opt, j) => (
                  <option key={j} value={opt}>{opt}</option>
                ))}
              </select>
            ) : field.fieldType === 'checkbox' ? (
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  required={field.required}
                  className="rounded border-zinc-300 text-[var(--color-primary,#4F46E5)] focus:ring-[var(--color-primary,#4F46E5)]"
                  checked={(values[field.label] as boolean) ?? false}
                  onChange={(e) => handleChange(field.label, e.target.checked)}
                />
                <span className="text-sm text-zinc-600">{field.placeholder || field.label}</span>
              </label>
            ) : (
              <input
                type={field.fieldType === 'email' ? 'email' : field.fieldType === 'phone' ? 'tel' : 'text'}
                required={field.required}
                placeholder={field.placeholder}
                className="rounded-lg border border-zinc-300 px-3 py-2 text-sm text-zinc-800 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary,#4F46E5)]"
                value={(values[field.label] as string) ?? ''}
                onChange={(e) => handleChange(field.label, e.target.value)}
              />
            )}
          </div>
        ))}

        {error && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="mt-2 px-6 py-2.5 rounded-lg bg-[var(--color-primary,#4F46E5)] text-white text-sm font-semibold hover:opacity-90 transition-opacity self-start disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {submitting && (
            <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4z" />
            </svg>
          )}
          {props.submitButtonText || 'Submit'}
        </button>
      </form>
    </div>
  );
}
